import { useEffect, useRef, useState } from "react";

import {
  fetchInheritanceByMode,
  flattenInheritance,
  type InheritanceMode,
  type SspInheritance,
} from "../api/frameworksApi";

interface UseSspInheritanceState {
  data: SspInheritance | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: SspInheritance = {
  applications: [],
  components: [],
  inventory: [],
  controls: [],
};

/**
 * Loads the inherited tree for the SSP creation flow and the SSP detail
 * page tabs. Pass null/undefined to skip the call (e.g. before the user
 * picks a Type).
 *
 *   mode="application" → /ssps/applications/components-with-assets
 *   mode="service"     → /ssps/services/components-with-assets
 *
 * Always returns an empty (but well-shaped) SspInheritance until the
 * request resolves so consumers can read .components.length without
 * null checks.
 *
 * The `refreshKey` argument lets callers force a fresh fetch even when
 * the mode hasn't changed — useful right after creating a new SSP, where
 * `isUsed`/`usedBySsps` would otherwise stay stale until the page is
 * remounted.
 */
export function useSspInheritance(
  mode: InheritanceMode | null | undefined,
  refreshKey?: unknown,
) {
  const [state, setState] = useState<UseSspInheritanceState>({
    data: null,
    loading: false,
    error: null,
  });
  const reqRef = useRef(0);

  useEffect(() => {
    if (!mode) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const reqId = ++reqRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchInheritanceByMode(mode)
      .then((apps) => {
        if (reqRef.current !== reqId) return;
        setState({ data: flattenInheritance(apps), loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (reqRef.current !== reqId) return;
        const message =
          err instanceof Error ? err.message : "Failed to load inheritance";
        setState({ data: null, loading: false, error: message });
      });
  }, [mode, refreshKey]);

  return {
    ...state,
    data: state.data ?? EMPTY,
  };
}
